import { useState } from 'react';
import { Link } from 'react-router-dom';
import { softwareList, softwareCategories, stats } from '@/mocks/softwareData';
import { articleList } from '@/mocks/articleData';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`text-xs ${
            star <= Math.floor(rating)
              ? 'ri-star-fill text-primary-500'
              : star - 0.5 <= rating
              ? 'ri-star-half-line text-primary-500'
              : 'ri-star-line text-foreground-300'
          }`}
        ></i>
      ))}
    </div>
  );
}

function SoftwareCard({ item }: { item: typeof softwareList[0] }) {
  return (
    <div className="group bg-background-50 border border-background-200/70 rounded-xl p-5 hover:border-primary-200 transition-all duration-200 cursor-pointer">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 group-hover:bg-primary-100 transition-colors">
          <i className={`${item.icon} text-2xl`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-base font-semibold text-foreground-950 whitespace-nowrap">{item.name}</h3>
            {item.isNew && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-accent-100 text-accent-700 rounded-md whitespace-nowrap">新品</span>
            )}
          </div>
          <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{item.description}</p>
          <div className="flex items-center gap-4 text-xs text-foreground-400 flex-wrap">
            <span className="flex items-center gap-1"><i className="ri-folder-line"></i>{item.category}</span>
            <span className="flex items-center gap-1"><i className="ri-hard-drive-line"></i>{item.size}</span>
            <span className="flex items-center gap-1"><i className="ri-download-2-line"></i>{item.downloads}</span>
          </div>
          <div className="flex items-center justify-between mt-3">
            <StarRating rating={item.rating} />
            <span className="text-xs font-medium text-primary-500 whitespace-nowrap">{item.rating}分</span>
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-background-200/70 flex items-center gap-3">
        <button className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
          <i className="ri-download-line text-base"></i>高速下载
        </button>
        <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-500 hover:text-foreground-700 hover:border-background-300 transition-colors cursor-pointer">
          <i className="ri-heart-line"></i>
        </button>
      </div>
    </div>
  );
}

export default function HomePage() {
  const featuredSoftware = softwareList.filter((s) => s.featured);
  const latestSoftware = softwareList.slice(0, 8);
  const gameSoftware = softwareList.filter((s) => s.category === '游戏').slice(0, 4);
  const latestArticles = articleList.slice(0, 3);

  return (
    <main>
      {/* Hero Section */}
      <section className="relative w-full min-h-[580px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20technology%20background%20with%20flowing%20curves%20and%20gradient%20shapes%2C%20warm%20orange%20and%20teal%20color%20palette%2C%20minimal%20modern%20design%2C%20soft%20overlapping%20circles%20and%20organic%20forms%2C%20clean%20digital%20aesthetic%2C%20light%20airy%20atmosphere%2C%20professional%20tech%20company%20style&width=1600&height=900&seq=hero-bg-91dl&orientation=landscape"
          alt="91下载背景"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/55"></div>
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-6">
            <i className="ri-shield-check-line text-accent-400"></i>
            <span className="text-sm text-white/90">100%安全检测 · 绿色无毒 · 极速下载</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight">
            极速安全的
            <span className="text-primary-400">软件下载</span>
            平台
          </h1>
          <p className="text-base md:text-lg text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
            汇聚全网优质资源，提供海量正版软件、游戏、工具类应用下载服务。安全无毒、绿色免费，让每一次下载更快、更稳、更放心。
          </p>
          <div className="max-w-xl mx-auto flex items-center bg-white/15 backdrop-blur-md rounded-2xl border border-white/20 p-2 gap-2">
            <div className="flex-1 flex items-center gap-2 px-3">
              <i className="ri-search-line text-white/60 text-lg"></i>
              <input
                type="text"
                placeholder="搜索你想要的软件、游戏..."
                className="flex-1 bg-transparent text-white placeholder-white/40 text-sm outline-none py-2"
              />
            </div>
            <button className="px-6 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              搜索
            </button>
          </div>
          <div className="flex items-center justify-center gap-6 mt-8 flex-wrap">
            {['微信', 'WPS', '剪映', '原神', '百度网盘', 'Chrome'].map((name) => (
              <span key={name} className="text-sm text-white/60 hover:text-white/90 cursor-pointer transition-colors whitespace-nowrap">
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="relative -mt-10 z-20 max-w-5xl mx-auto px-4 md:px-6">
        <div className="bg-background-50 rounded-2xl border border-background-200/70 py-6 px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 mb-3">
                <i className={`${stat.icon} text-lg`}></i>
              </div>
              <div className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{stat.value}</div>
              <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Software */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pt-16 pb-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">精选推荐</h2>
            <p className="text-sm text-foreground-500 mt-1">编辑精心挑选的优质软件</p>
          </div>
          <Link
            to="/category"
            className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {featuredSoftware.slice(0, 6).map((item) => (
            <SoftwareCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      {/* Category Quick Links */}
      <section className="bg-background-100 py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">软件分类</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {softwareCategories.filter((c) => c !== '全部').map((cat) => (
              <Link
                key={cat}
                to="/category"
                className="flex flex-col items-center gap-3 p-5 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 hover:-translate-y-1 transition-all duration-200 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-500">
                  <i className={`${getCategoryIcon(cat)} text-xl`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Software */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">最新上架</h2>
            <p className="text-sm text-foreground-500 mt-1">实时更新热门软件资源</p>
          </div>
          <Link
            to="/category"
            className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
          >
            更多软件 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {latestSoftware.map((item) => (
            <SoftwareCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      {/* Game Zone */}
      <section className="bg-background-100 py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">游戏专区</h2>
              <p className="text-sm text-foreground-500 mt-1">热门游戏一键下载，畅享游戏乐趣</p>
            </div>
            <Link
              to="/category"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
            >
              全部游戏 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {gameSoftware.map((item) => (
              <SoftwareCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* Article Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">下载资讯</h2>
            <p className="text-sm text-foreground-500 mt-1">软件评测、使用教程、安全指南</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {latestArticles.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-2 py-1 text-[11px] font-medium bg-accent-50 text-accent-700 rounded-md">{article.category}</span>
                  <span className="text-xs text-foreground-400">{article.publishDate}</span>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                  {article.title}
                </h3>
                <p className="text-xs text-foreground-500 line-clamp-2 mb-4">{article.excerpt}</p>
                <div className="flex items-center justify-between text-xs text-foreground-400">
                  <span><i className="ri-eye-line mr-1"></i>{article.readCount} 阅读</span>
                  <span>{article.author}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Trust Banner */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
        <div className="bg-background-100 rounded-2xl border border-background-200/70 p-8 md:p-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-50 text-accent-700 rounded-full text-sm font-medium mb-6">
            <i className="ri-shield-check-line"></i> 安全承诺
          </div>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
            为什么选择 <span className="text-primary-500">91下载</span>？
          </h2>
          <p className="text-sm text-foreground-600 max-w-2xl mx-auto mb-10 leading-relaxed">
            我们与国内外知名软件厂商合作，所有上架软件均经过严格安全检测，确保无毒无捆绑。高速下载通道配合智能CDN加速，让下载体验更快更稳。
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: 'ri-shield-flash-line', title: '安全无毒', desc: '多重引擎扫描检测，杜绝病毒木马' },
              { icon: 'ri-rocket-2-line', title: '极速下载', desc: '智能CDN加速，满速下载不等待' },
              { icon: 'ri-customer-service-2-line', title: '贴心服务', desc: '7x24小时客服支持，问题随时解决' },
            ].map((item) => (
              <div key={item.title} className="text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-50 text-accent-500 mb-3">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-1">{item.title}</h3>
                <p className="text-xs text-foreground-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '91下载',
            url: 'https://www.91xiazai.com/',
            description: '91下载是您最信赖的软件资源下载平台，提供海量正版软件、游戏、工具类应用的极速下载服务。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://www.91xiazai.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
    </main>
  );
}

function getCategoryIcon(cat: string): string {
  const map: Record<string, string> = {
    '办公软件': 'ri-briefcase-line',
    '多媒体': 'ri-film-line',
    '游戏': 'ri-gamepad-line',
    '实用工具': 'ri-tools-line',
    '影音娱乐': 'ri-music-2-line',
    '社交沟通': 'ri-chat-3-line',
    '开发工具': 'ri-code-line',
    '浏览器': 'ri-compasses-line',
    '安全防护': 'ri-shield-check-line',
  };
  return map[cat] || 'ri-apps-line';
}